import React, { useState } from 'react';
import { Plus, X, Send, Loader2, CheckCircle2, XCircle } from 'lucide-react';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";

const genId = () => `wh_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * Form-level webhook configuration (Phase 8) — every enabled entry gets a
 * POST of { event: 'form.submitted', formId, submissionId, values, timestamp }
 * on each real submission, signed with HMAC-SHA256 in X-Webhook-Signature
 * when a secret is set (see FormWebhookService.js — same scheme as the
 * support widget's webhooks, so a receiver verifies both the same way).
 * "Send Test" hits an authenticated endpoint that reuses that exact
 * delivery code path rather than a separate bespoke ping.
 */
export default function WebhooksPanel({ webhooks, onChange, onTest, onClose }) {
  const list = webhooks || [];
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});

  const update = (next) => onChange(next);

  const addWebhook = () => {
    update([...list, { id: genId(), url: '', secret: '', enabled: true }]);
  };
  const updateWebhook = (id, patch) => {
    update(list.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  };
  const removeWebhook = (id) => {
    update(list.filter((w) => w.id !== id));
  };

  const sendTest = async (webhook) => {
    if (!webhook.url) return;
    setTesting((prev) => ({ ...prev, [webhook.id]: true }));
    setTestResults((prev) => ({ ...prev, [webhook.id]: null }));
    try {
      const result = await onTest(webhook);
      setTestResults((prev) => ({ ...prev, [webhook.id]: result }));
    } catch (err) {
      setTestResults((prev) => ({ ...prev, [webhook.id]: { ok: false, error: err.message } }));
    } finally {
      setTesting((prev) => ({ ...prev, [webhook.id]: false }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[480px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <p className="text-[16px] font-bold text-[#2E2E2E]">Webhooks</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        <p className="text-[11px] text-[#706E6B] mb-4">
          Send each submission's answers to an external URL as JSON. Add a secret to have the
          payload signed (HMAC-SHA256, header <code className="text-[10px]">X-Webhook-Signature</code>)
          so your receiver can verify it came from this form.
        </p>

        <div className="space-y-4">
          {list.map((webhook) => {
            const result = testResults[webhook.id];
            return (
              <div key={webhook.id} className="border border-[#D8DDE6] rounded-[4px] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-[12px] font-bold text-[#181818] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!webhook.enabled}
                      onChange={(e) => updateWebhook(webhook.id, { enabled: e.target.checked })}
                      className="accent-[#0176D3] w-4 h-4"
                    />
                    Enabled
                  </label>
                  <button onClick={() => removeWebhook(webhook.id)} className="p-1 text-[#706E6B] hover:text-red-500">
                    <X size={14} />
                  </button>
                </div>

                <div>
                  <label className={labelClass}>URL</label>
                  <input
                    className={inputClass}
                    value={webhook.url || ''}
                    onChange={(e) => updateWebhook(webhook.id, { url: e.target.value })}
                    placeholder="https://example.com/webhook"
                  />
                </div>

                <div>
                  <label className={labelClass}>Secret (optional)</label>
                  <input
                    className={inputClass}
                    value={webhook.secret || ''}
                    onChange={(e) => updateWebhook(webhook.id, { secret: e.target.value })}
                    placeholder="Used to sign the payload"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => sendTest(webhook)}
                    disabled={!webhook.url || testing[webhook.id]}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] text-[12px] font-bold text-[#0176D3] border border-[#0176D3]/40 hover:bg-[#0176D3]/5 disabled:opacity-50 whitespace-nowrap"
                  >
                    {testing[webhook.id] ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    Send Test
                  </button>
                  {result && (
                    <span className={`flex items-center gap-1 text-[11px] font-bold ${result.ok ? 'text-[#04844B]' : 'text-[#EA001E]'}`}>
                      {result.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                      {result.ok ? `Delivered (${result.statusCode})` : (result.error || `Status ${result.statusCode}`)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={addWebhook}
          className="mt-4 flex items-center gap-1.5 text-[12px] font-bold text-[#0176D3] hover:text-[#015BA0]"
        >
          <Plus size={14} /> Add Webhook
        </button>
      </div>
    </div>
  );
}
